import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Smartphone } from "lucide-react";
import { testersApi, formatApiError } from "../lib/api";

const STEPS = [
  "Sign up below with the Gmail address that's signed in on your Android phone.",
  "We add you to the WHAT? test group in Google Play (usually within a day or two).",
  "You get a link to install WHAT? from the Play Store. Keep it installed for 14 days and tell us what you think.",
];

const Done = ({ already }) => (
  <div data-testid="tester-success" className="what-tester-card text-center py-12">
    <CheckCircle2 size={44} className="mx-auto text-[#D4FF3A]" />
    <h2 className="font-display text-2xl font-bold text-white mt-4">{already ? "You're already on the list!" : "You're on the list!"}</h2>
    <p className="text-slate-400 mt-2 max-w-sm mx-auto">We'll add your Gmail to the test group and send you the Play Store link soon. Thank you for helping.</p>
  </div>
);

export default function TesterPage() {
  const [form, setForm] = useState({ name: "", gmail: "", instagram: "", agree: false });
  const [state, setState] = useState({ busy: false, error: "", done: null });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, error: "", done: null });
    try {
      const r = await testersApi.join(form);
      setState({ busy: false, error: "", done: r });
    } catch (err) {
      setState({ busy: false, error: formatApiError(err.response?.data?.detail), done: null });
    }
  };
  return (
    <div data-testid="tester-page" className="min-h-screen bg-[#0B0F17] text-slate-300">
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-14 grid lg:grid-cols-[1.1fr_1fr] gap-14 items-start">
        <div className="what-rise">
          <Link to="/" data-testid="tester-home-link" className="font-display text-2xl font-extrabold text-white">WHAT?</Link>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#D4FF3A] mt-10 flex items-center gap-2"><Smartphone size={14} /> Android early access</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white mt-4 leading-[1.02]">Test WHAT? before anyone else.</h1>
          <p className="text-base text-slate-400 mt-6 max-w-md">Three AIs debate your questions and agree on the best answer. Help us launch on Google Play by trying the app on your Android phone.</p>
          <ol className="mt-10 space-y-5">
            {STEPS.map((s, i) => (
              <li key={s} className="flex gap-4"><span className="what-step-num">{i + 1}</span><span className="text-sm text-slate-300 pt-1">{s}</span></li>
            ))}
          </ol>
        </div>
        {state.done ? <Done already={state.done.already} /> : (
          <form onSubmit={submit} data-testid="tester-form" className="what-tester-card space-y-5 what-rise [animation-delay:120ms]">
            <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">Your name
              <input data-testid="tester-name-input" value={form.name} onChange={set("name")} required maxLength={80} className="what-input mt-2" />
            </label>
            <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">Gmail on your Android phone
              <input data-testid="tester-gmail-input" type="email" value={form.gmail} onChange={set("gmail")} required placeholder="you@gmail.com" className="what-input mt-2" />
            </label>
            <label className="block text-xs font-mono uppercase tracking-[0.14em] text-slate-500">Instagram (optional)
              <input data-testid="tester-instagram-input" value={form.instagram} onChange={set("instagram")} maxLength={40} placeholder="@yourname" className="what-input mt-2" />
            </label>
            <label className="flex items-start gap-3 text-sm text-slate-400">
              <input data-testid="tester-agree-checkbox" type="checkbox" checked={form.agree} onChange={set("agree")} className="mt-1 accent-[#D4FF3A]" />
              <span>I'm 18 or older and agree to test WHAT? on Android. My name and Gmail are used only to add me to the test group (see the <a href="/privacy" className="text-[#D4FF3A] hover:underline">privacy policy</a>).</span>
            </label>
            {state.error && <p data-testid="tester-error" className="text-sm text-red-300">{state.error}</p>}
            <button data-testid="tester-submit-btn" disabled={state.busy || !form.agree} className="what-btn-primary w-full flex items-center justify-center gap-2">
              {state.busy && <Loader2 size={16} className="animate-spin" />} Sign me up
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
